// Contexto de autenticação própria (CPF + Senha).
// Não utiliza Lumi Auth nem OTP. A sessão fica apenas na aba aberta (sessionStorage),
// por isso não permanece guardada no navegador depois de fechar o separador.

import { createContext, useContext, useEffect, useState } from 'react'
import { lumi } from '@/lib/lumi'
import { unmaskCPF, verifyPassword } from '@/utils/masks'
import { garantirChaveUsuario } from '@/utils/migrarAssinaturas'

export interface AppUser {
  _id: string
  userId: string
  nomeCompleto: string
  cpf: string
  telefone?: string
  email?: string
  funcao?: string
  matricula?: string
  fotoUrl?: string
  cidadeId?: string
  secretariaId?: string
  subLocalIds?: string[]
  role: 'ADMIN' | 'MODERADOR' | 'GESTOR' | 'COORDENADOR'
  status: 'ativo' | 'inativo'
  isSystemAdmin?: boolean
  chaveAssinatura?: string
}

interface AuthContextValue {
  user: AppUser | null
  isAuthenticated: boolean
  isLoading: boolean
  login: (cpf: string, senha: string) => Promise<{ success: boolean, message?: string }>
  logout: () => void
  refreshUser: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined)
const STORAGE_KEY = 'protocoleai_session_userid'

// sessionStorage: apagado automaticamente ao fechar o separador/navegador.
const sessao = {
  get: () => sessionStorage.getItem(STORAGE_KEY),
  set: (v: string) => sessionStorage.setItem(STORAGE_KEY, v),
  clear: () => {
    sessionStorage.removeItem(STORAGE_KEY)
    localStorage.removeItem(STORAGE_KEY)
  },
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AppUser | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  const loadFromStorage = async () => {
    const savedUserId = sessao.get()
    if (!savedUserId) {
      setIsLoading(false)
      return
    }
    try {
      const r = await (lumi.entities as any).user_profiles.list({
        filter: { userId: savedUserId },
        limit: 1,
      })
      if (r?.list?.length) {
        setUser(r.list[0])
      }
      else {
        sessao.clear()
      }
    }
    catch {
      /* silencioso */
    }
    finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    loadFromStorage()
  }, [])

  const login = async (cpfRaw: string, senha: string) => {
    const cpf = unmaskCPF(cpfRaw)
    try {
      const r = await (lumi.entities as any).user_profiles.list({
        filter: { cpf },
        limit: 1,
      })
      const found = r?.list?.[0]
      if (!found)
        return { success: false, message: 'CPF ou senha inválidos.' }

      if (found.status !== 'ativo')
        return { success: false, message: 'Utilizador inativo. Contacte o administrador.' }

      const valid = await verifyPassword(senha, found.passwordHash || '')
      if (!valid)
        return { success: false, message: 'CPF ou senha inválidos.' }

      sessao.set(found.userId)
      setUser(found)
      // Garante a chave única de assinatura do utilizador
      garantirChaveUsuario(found)
      return { success: true }
    }
    catch {
      return { success: false, message: 'Erro ao autenticar. Tente novamente.' }
    }
  }

  const logout = () => {
    sessao.clear()
    setUser(null)
  }

  const refreshUser = async () => {
    await loadFromStorage()
  }

  return (
    <AuthContext.Provider value={{ user, isAuthenticated: !!user, isLoading, login, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAppAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx)
    throw new Error('useAppAuth deve ser usado dentro de AuthProvider')
  return ctx
}
